import React, { useEffect, useMemo, useState } from 'react';
import { Image } from 'react-native';
import { Flex, RemoteImage, Typography, useScreenSize } from '@banhala/ui-kit';
import type { GoodsPiece } from 'shared/src/api/goodsService';
import { useGetMeasureValues } from '../hooks/useGetMeasureValues';
import { MeasurementSizeDetailTable } from './MeasurementSizeDetailTable';

interface Props {
  piece: GoodsPiece;
}

export const MeasurementTop: React.FC<Props> = ({ piece }) => {
  const getMeasureValues = useGetMeasureValues();
  const { measureValues, dataType, goodsExtractedMeasureImage } =
    getMeasureValues(piece);

  const isShowAIExtractInformMessage = dataType !== 'SELLER_TABLE';

  const emptyMeasurementSizes =
    piece.measurement.sizes.length === 0 &&
    piece.measurement.extracted_sizes.length === 0;

  const { width: screenWidth } = useScreenSize();
  const containerWidth = screenWidth - 32;

  const [imageSize, setImageSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  // 이미지 원본 크기 가져오기
  useEffect(() => {
    if (piece.product_category.image_placeholder) {
      Image.getSize(
        piece.product_category.image_placeholder,
        (width, height) => {
          setImageSize({ width, height });
        },
        () => {
          setImageSize(null);
        },
      );
    }
  }, [piece.product_category.image_placeholder]);

  // 원본 로직과 동일하게 측정 스팟 위치 계산
  const measurementSpots = useMemo(() => {
    if (emptyMeasurementSizes) return [];
    if (imageSize == null) return [];

    const baseWidth = 340;
    const { width: naturalWidth, height: naturalHeight } = imageSize;
    const scaledHeight =
      (naturalHeight * ((baseWidth / naturalWidth) * 100)) / 100;

    return measureValues.map(({ value, spot }) => ({
      value,
      spot: {
        ...spot,
        // bottom을 퍼센트로 계산 (원본 로직)
        bottomPercent:
          ((-((spot.position + 1) * 12) + Math.abs(spot.top)) / scaledHeight) *
          100,
        // left를 퍼센트로 계산
        leftPercent: (spot.left / baseWidth) * 100,
      },
    }));
  }, [emptyMeasurementSizes, measureValues, imageSize]);

  const containerHeight = useMemo(() => {
    if (!imageSize) return containerWidth;
    return (imageSize.height / imageSize.width) * containerWidth;
  }, [imageSize, containerWidth]);

  if (measureValues.length === 0 && !goodsExtractedMeasureImage) {
    return null;
  }

  return (
    <Flex gap={24}>
      <Flex gap={16}>
        <Flex gap={2}>
          <Typography type={'h5'} color={'content_primary'}>
            {isShowAIExtractInformMessage ? 'AI ' : ''}실측 사이즈
          </Typography>
          {isShowAIExtractInformMessage && (
            <Typography type={'body3'} color={'content_tertiary'}>
              상세 페이지를 스캔해 실측정보를 찾아왔어요!
            </Typography>
          )}
        </Flex>

        {goodsExtractedMeasureImage ? (
          <RemoteImage
            uri={goodsExtractedMeasureImage}
            width={containerWidth}
            height={containerWidth}
            resizeMode="contain"
          />
        ) : (
          <MeasurementSizeDetailTable piece={piece} />
        )}

        {isShowAIExtractInformMessage && (
          <Flex.Row gap={4} padding={{ top: 16 }}>
            <Typography type={'body4'} color={'content_secondary'}>
              스캔 과정에서 오류가 발생하거나, 상품 판매자가 정보를 수정하는
              경우 부정확한 정보가 있을 수 있으므로 참고용으로만 봐 주세요.
            </Typography>
          </Flex.Row>
        )}
      </Flex>

      {!emptyMeasurementSizes && piece.product_category.image_placeholder && (
        <Flex
          position={'relative'}
          width={containerWidth}
          height={containerHeight}
          overflow={'hidden'}
        >
          <RemoteImage
            width={containerWidth}
            height={containerHeight}
            uri={piece.product_category.image_placeholder}
            resizeMode="contain"
          />
          {measurementSpots.map(({ spot }) => (
            <Flex
              key={spot.sno}
              position={'absolute'}
              width={'100%'}
              bottom={`${spot.bottomPercent}%`}
              left={`${spot.leftPercent}%`}
            >
              <Typography type={'subtitle5'} textAlign={'center'}>
                {spot.name}
              </Typography>
            </Flex>
          ))}
        </Flex>
      )}
    </Flex>
  );
};
